// Config-driven rendering for the public PWHL standings table.
// Maps every catalogue column key -> { get, sort, cls } so the table can be
// composed from a saved admin config per device.
import { useEffect, useState } from "react";
import { STANDINGS_COLS, DEFAULT_STANDINGS_COLS, baseKey } from "./standingsColumns";

const pct = (v) => (v || v === 0) ? `${(v * 100).toFixed(1)}` : "—";
const per = (n, d) => (d ? (n / d).toFixed(2) : "—");
const signed = (v) => (v > 0 ? `+${v}` : `${v}`);
const streakNum = (st) => { if (!st || st === "-") return 0; const n = parseInt(String(st).slice(1), 10) || 0; return String(st)[0] === "W" ? n : -n; };

// key -> renderer. `get` returns display text; `sort` a number; `cls(s)` optional colour.
export const STAND_RENDER = {
    gp: { get: (s) => s.gp ?? 0, sort: (s) => s.gp || 0 },
    gr: { get: (s) => s.games_remaining ?? 0, sort: (s) => s.games_remaining || 0 },
    mag: { get: (s) => (s._mag === "clinched" || s._mag === "elim" ? 0 : s._mag == null ? "—" : s._mag), sort: (s) => (typeof s._mag === "number" ? s._mag : (s._mag === "clinched" ? -2 : s._mag === "elim" ? 9998 : 9999)), nowrap: true, cls: () => "text-white" },
    gold_pts: { get: (s) => (s._goldPts == null ? "—" : s._goldPts), sort: (s) => (s._goldPts == null ? -1 : s._goldPts), nowrap: true, bold: true, cls: (s) => (s._goldTop ? "text-[#F6C453]" : s._goldPts == null ? "text-white/30" : "text-white") },
    record: { get: (s) => s.record || "—", sort: (s) => s.pts || 0, nowrap: true },
    shootout: { get: (s) => s.shootout || "—", sort: (s) => (s.sow || 0) - (s.sol || 0), nowrap: true, muted: true },
    ptspct: { get: (s) => (s.gp ? ((s.pts || 0) / (s.gp * 3)).toFixed(3) : "—"), sort: (s) => (s.gp ? (s.pts || 0) / (s.gp * 3) : 0) },
    streak: { get: (s) => s.streak || "-", sort: (s) => streakNum(s.streak), bold: true, cls: (s) => (String(s.streak || "").startsWith("W") ? "text-[#22C55E]" : (s.streak || "-") === "-" ? "text-white/30" : "text-[#FB7185]") },
    l10: { get: (s) => s.l10 || "—", sort: (s) => streakNum(s.l10), nowrap: true },
    home_rec: { get: (s) => s.home_record || "—", sort: (s) => 0, nowrap: true },
    away_rec: { get: (s) => s.away_record || "—", sort: (s) => 0, nowrap: true },
    gfga: { get: (s) => `${s.gf ?? 0}-${s.ga ?? 0}`, sort: (s) => (s.gf || 0) - (s.ga || 0), nowrap: true },
    gf_gp: { get: (s) => per(s.gf || 0, s.gp), sort: (s) => (s.gp ? (s.gf || 0) / s.gp : 0) },
    ga_gp: { get: (s) => per(s.ga || 0, s.gp), sort: (s) => (s.gp ? (s.ga || 0) / s.gp : 0) },
    gd: { get: (s) => signed(s.gd || 0), sort: (s) => s.gd || 0 },
    pts: { get: (s) => s.pts ?? 0, sort: (s) => s.pts || 0 },
    w: { get: (s) => s.w ?? 0, sort: (s) => s.w || 0 },
    otw: { get: (s) => s.otw ?? 0, sort: (s) => s.otw || 0 },
    otl: { get: (s) => s.otl ?? 0, sort: (s) => s.otl || 0 },
    l: { get: (s) => s.l ?? 0, sort: (s) => s.l || 0 },
    gf: { get: (s) => s.gf ?? 0, sort: (s) => s.gf || 0 },
    ga: { get: (s) => s.ga ?? 0, sort: (s) => s.ga || 0 },
    sow: { get: (s) => s.sow ?? 0, sort: (s) => s.sow || 0 },
    sol: { get: (s) => s.sol ?? 0, sort: (s) => s.sol || 0 },
    pp: { get: (s) => pct(s.pp_pct), sort: (s) => s.pp_pct || 0 },
    pk: { get: (s) => pct(s.pk_pct), sort: (s) => s.pk_pct || 0 },
    ppop: { get: (s) => s.ppop ?? 0, sort: (s) => s.ppop || 0 },
    ppgf: { get: (s) => s.ppgf ?? 0, sort: (s) => s.ppgf || 0 },
    tsh: { get: (s) => s.tsh ?? 0, sort: (s) => s.tsh || 0 },
    // PP GA = goals allowed DURING our power play (opponent short-handed goals) → s.shga.
    // SH GA = goals allowed while WE are short-handed (opponent power-play goals) → s.ppga.
    ppga: { get: (s) => s.shga ?? 0, sort: (s) => s.shga || 0 },
    shgf: { get: (s) => s.shgf ?? 0, sort: (s) => s.shgf || 0 },
    shga: { get: (s) => s.ppga ?? 0, sort: (s) => s.ppga || 0 },
    esgf: { get: (s) => s.esgf ?? 0, sort: (s) => s.esgf || 0 },
    esga: { get: (s) => s.esga ?? 0, sort: (s) => s.esga || 0 },
    engf: { get: (s) => s.engf ?? 0, sort: (s) => s.engf || 0 },
    enga: { get: (s) => s.enga ?? 0, sort: (s) => s.enga || 0 },
    ps: { get: (s) => `${s.psgf ?? 0}/${s.psga ?? 0}`, sort: (s) => s.psgf || 0, nowrap: true },
    fg: { get: (s) => s.fg ?? 0, sort: (s) => s.fg || 0 },
    lg: { get: (s) => s.lg ?? 0, sort: (s) => s.lg || 0 },
    shots: { get: (s) => s.shots ?? 0, sort: (s) => s.shots || 0 },
    shpct: { get: (s) => pct(s.sh_pct), sort: (s) => s.sh_pct || 0 },
    fo: { get: (s) => s.fo_won ?? 0, sort: (s) => s.fo_won || 0 },
    fopct: { get: (s) => pct(s.fo_pct), sort: (s) => s.fo_pct || 0 },
    hits: { get: (s) => s.hits ?? 0, sort: (s) => s.hits || 0 },
    blk: { get: (s) => s.blocks ?? 0, sort: (s) => s.blocks || 0 },
    pim: { get: (s) => s.pim ?? 0, sort: (s) => s.pim || 0 },
    no: { get: (s) => s.next_opponent || "", sort: () => 0, nowrap: true },
};

export const COL_LABEL = Object.fromEntries(STANDINGS_COLS.map((c) => [c.key, c.label]));
export const COL_TITLE = Object.fromEntries(STANDINGS_COLS.map((c) => [c.key, c.title]));

// Only the Team column is locked (always shown, always first). Everything else —
// including P (points) — is a normal, reorderable, configurable column.
export const LOCKED_COLS = ["team"];
// Default per-device column sets. "pts" now lives in the list (it is no longer
// locked/rendered specially); the Team column is implicit and always first.
export const DEFAULT_DEVICE_COLS = {
    desktop: ["pts", "gp", "gr", "mag", "record", "shootout", "ptspct", "streak", "l10", "home_rec", "away_rec", "gfga", "gd", "pp", "pk"],
    tablet: ["pts", "gp", "mag", "record", "ptspct", "gd", "streak", "l10"],
    mobile: ["pts", "gp", "record", "gd"],
};

// Inject the MAG# (Required) column into a resolved list when absent — placed
// right after GR (else after PTS). Skipped on mobile (too cramped). Mirrors the
// pts soft-migration so existing saved configs surface the new column.
export function withMag(list, device) {
    if (device === "mobile" || list.includes("mag")) return list;
    const out = [...list];
    let at = out.indexOf("gr");
    if (at < 0) at = out.indexOf("pts");
    out.splice(at < 0 ? 0 : at + 1, 0, "mag");
    return out;
}

// Inject the NO (Next Opponent) column at the end when absent, so it stays visible
// for pre-existing saved configs while remaining a normal, reorderable column.
export function withNo(list) {
    return list.includes("no") ? list : [...list, "no"];
}

export function resolveDeviceCols(devices, device, migrated = true) {
    const valid = new Set(STANDINGS_COLS.map((c) => c.key));
    const stored = devices && Array.isArray(devices[device]) ? devices[device] : null;
    const list = (stored && stored.length ? stored : DEFAULT_DEVICE_COLS[device]) || DEFAULT_DEVICE_COLS.desktop;
    let out = list.filter((k) => valid.has(baseKey(k)) && k !== "team");
    // One-time migration: pre-unlock configs never contained "pts" (it used to be
    // locked/rendered separately). Until an admin re-saves through the new editor,
    // inject it at the front so points still show. Once saved, the list is verbatim.
    if (!migrated && !out.includes("pts")) out = ["pts", ...out];
    return out;
}

// Per-column, per-device display overrides (name/tooltip/bold/text-size/width).
export function resolveColOverrides(settings, device) {
    const all = (settings && settings.standings_col_overrides) || {};
    return all[device] || {};
}

// Merge a column's saved overrides with its catalogue defaults.
export function colMeta(overrides, key, defLabel, defTitle) {
    const o = (overrides && overrides[key]) || {};
    return {
        label: (o.name != null && String(o.name).trim() !== "") ? o.name : defLabel,
        title: (o.tooltip != null && String(o.tooltip).trim() !== "") ? o.tooltip : defTitle,
        bold: !!o.bold,
        size: o.size ? Number(o.size) : null,    // px
        width: o.width ? Number(o.width) : null,  // px
        group: (o.group != null && String(o.group).trim() !== "") ? String(o.group).trim() : "",
    };
}

// ---- Sizing tiers ----
export const STAND_SIZE = {
    xs: { logo: "h-5 w-5", rank: "text-sm", name: "text-[11px]", stat: "text-[10px]", pts: "text-sm", py: "py-1", gap: "gap-1.5", status: "text-[9px]" },
    compact: { logo: "h-6 w-6 sm:h-7 sm:w-7", rank: "text-base sm:text-lg", name: "text-[13px]", stat: "text-[11px]", pts: "text-base sm:text-lg", py: "py-2", gap: "gap-2", status: "text-[11px]" },
    default: { logo: "h-8 w-8 sm:h-9 sm:w-9", rank: "text-lg sm:text-2xl", name: "text-[15px]", stat: "text-[13px]", pts: "text-lg sm:text-xl", py: "py-3.5", gap: "gap-3", status: "text-xs sm:text-sm" },
    large: { logo: "h-10 w-10 sm:h-12 sm:w-12", rank: "text-2xl sm:text-3xl", name: "text-[17px]", stat: "text-[15px]", pts: "text-xl sm:text-2xl", py: "py-4", gap: "gap-3.5", status: "text-sm sm:text-base" },
};
export const SCHED_SIZE = {
    compact: { h: "h-8", logo: "h-3.5 w-3.5", abbr: "text-[10px]", score: "text-[11px]", status: "text-[9px]", statusW: "w-9" },
    default: { h: "h-9", logo: "h-4 w-4", abbr: "text-[11px]", score: "text-[12px]", status: "text-[10px]", statusW: "w-11" },
    large: { h: "h-11", logo: "h-5 w-5", abbr: "text-[13px]", score: "text-[14px]", status: "text-[11px]", statusW: "w-12" },
};

export function resolveSize(sizeCfg, device, table) {
    const map = table === "schedule" ? SCHED_SIZE : STAND_SIZE;
    const d = (sizeCfg && sizeCfg[device]) || {};
    const tier = map[d.tier] ? d.tier : "default";
    return { tier, bold: !!d.bold, s: map[tier], rowH: d.rowH ? Number(d.rowH) : null, abbr: !!d.abbr };
}

// Preset px options admins pick from (so they never type raw dimensions).
// "" = Auto (column auto-width / tier-driven row height).
export const COL_WIDTH_PRESETS = [16, 20, 24, 28, 32, 40, 48, 56, 64, 72, 80, 100, 120, 150, 200];
export const ROW_HEIGHT_PRESETS = [28, 32, 36, 40, 44, 48, 56, 64];

// ---- Device detection ----
export function deviceFromWidth(w) {
    if (w >= 1024) return "desktop";
    if (w >= 640) return "tablet";
    return "mobile";
}

export function useDeviceKind() {
    const [device, setDevice] = useState(() => (typeof window !== "undefined" ? deviceFromWidth(window.innerWidth) : "desktop"));
    useEffect(() => {
        const onResize = () => setDevice(deviceFromWidth(window.innerWidth));
        window.addEventListener("resize", onResize);
        onResize();
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return device;
}

// ---- Per-VIEW config (per stage / all-time / gold) layered on top of per-device ----
// view ∈ regular | playoffs | preseason | all-time | gold. Each view can have its
// own column set, overrides and sizing per device; anything not configured for a
// view falls back to the global (legacy) standings config, then to defaults.
export const STANDINGS_VIEWS = [
    { k: "full", l: "Overall" },
    { k: "home", l: "Home" },
    { k: "away", l: "Away" },
    { k: "l10", l: "Last 10" },
    { k: "streak", l: "Streak" },
    { k: "predict", l: "Predict" },
];

export function resolveViewCols(settings, view, device, migrated = true) {
    const valid = new Set(STANDINGS_COLS.map((c) => c.key));
    const vc = (settings && settings.standings_view_columns) || {};
    const stored = vc[view] && Array.isArray(vc[view][device]) ? vc[view][device] : null;
    const addMag = (view === "full");
    if (stored && stored.length) {
        const out = stored.filter((k) => valid.has(baseKey(k)) && k !== "team");
        return withNo(addMag ? withMag(out, device) : out);
    }
    const fallback = resolveDeviceCols(settings && settings.standings_columns_devices, device, migrated);
    return withNo(addMag ? withMag(fallback, device) : fallback);
}

export function resolveViewOverrides(settings, view, device) {
    const vo = (settings && settings.standings_view_overrides) || {};
    if (vo[view] && vo[view][device]) return vo[view][device];
    return resolveColOverrides(settings, device);
}

export function resolveViewSize(settings, view, device) {
    const vs = (settings && settings.standings_view_size) || {};
    const cfg = vs[view] ? vs[view] : (settings && settings.standings_size);
    return resolveSize(cfg, device, "standings");
}


// ---- Game-Stage (period) + Rate transforms for the Standings table ----
// Mirrors the Leaderboards/Game-Centre toggles, but only touches columns where a
// period / rate value actually MAKES SENSE (goal-based counts). Records, points,
// GP, streak, L10 and the special-teams PERCENTAGES stay season-based.
// Per-period tint colours — MUST match the Leaderboards / Game-Centre PERIOD_META.
export const STANDINGS_PERIOD_COLORS = {
    combined: "#38BDF8", regulation: "#38BDF8", p1: "#5BD1B7", p2: "#F6C453",
    p3: "#FB7185", ot: "#9D4CDD", so: "#F472B6",
};
export const STANDINGS_RATE_COLOR = "#5EEAD4";
export const STANDINGS_PERIOD_TOGGLES = [
    { k: "combined", l: "Combined" }, { k: "regulation", l: "Regulation" },
    { k: "p1", l: "P1" }, { k: "p2", l: "P2" }, { k: "p3", l: "P3" },
    { k: "ot", l: "OT" }, { k: "so", l: "SO" },
];
export const STANDINGS_RATE_MODES = [
    { k: "total", l: "TOTAL" }, { k: "gp", l: "/GP" }, { k: "s", l: "/S" }, { k: "p60", l: "/60" },
];
const _PERIOD_MAP = { p1: ["1"], p2: ["2"], p3: ["3"], ot: ["4"], so: ["5"], regulation: ["1", "2", "3"] };
// Only GOAL-based columns have a per-period source (scoring events / g_p1..p3) — same
// as Leaderboards. Shots/FO/PIM/hits/blocks are game totals with NO period split.
const _GOAL_FIELDS = ["gf", "ga", "ppgf", "ppga", "shgf", "shga", "esgf", "esga", "engf", "enga", "psgf", "psga"];
// Rate works on any counting stat (season total ÷ GP / shots / 60) regardless of period.
const _RATE_FIELDS = [..._GOAL_FIELDS, "shots", "fo", "fow", "hits", "blk", "pim", "fg", "lg"];

// ---- Shared Magic # + clinch (z/x/e) for the Standings STORY template --------
// Rows need: team_id, pts, gf, ga, gp, rem (games remaining). Sets:
//   r.clinch  "z" (clinched league / 1st overall) | "x" (clinched playoff berth)
//             | "e" (eliminated) | "" (still in the hunt)
//   r.magic   points still needed to CLINCH A PLAYOFF BERTH — ONLY that race:
//             0 once a berth is clinched (x), null (—) for z / e / not-started,
//             else the live countdown number. No league-clinch, no "e" here.
export function applyMagicClinch(rows, berths = 4) {
    if (!Array.isArray(rows) || !rows.length) return rows;
    const remOf = (r) => Math.max(0, r.rem || 0);
    const byPts = [...rows].sort((a, b) => (b.pts - a.pts) || (((b.gf || 0) - (b.ga || 0)) - ((a.gf || 0) - (a.ga || 0))));
    const chaser = byPts[berths];            // first team OUT of the berths
    const lastIn = byPts[berths - 1];        // last team IN the berths
    const chaserMax = chaser ? (chaser.pts || 0) + remOf(chaser) * 3 : -1;
    // z = leader mathematically uncatchable by everyone else.
    const leader = byPts[0];
    let clinchedLeagueId = null;
    if (leader && (leader.pts || 0) > 0) {
        const caught = byPts.slice(1).some((o) => (o.pts || 0) + remOf(o) * 3 >= (leader.pts || 0));
        if (!caught) clinchedLeagueId = leader.team_id;
    }
    byPts.forEach((s, i) => {
        const pos = i + 1;
        const teamMax = (s.pts || 0) + remOf(s) * 3;
        const notStarted = (s.gp || 0) === 0;
        let clinch = "";
        if (s.team_id === clinchedLeagueId) clinch = "z";
        else if ((pos <= berths && chaser && (s.pts || 0) > chaserMax) || !chaser) clinch = "x";
        else if (pos > berths && lastIn && teamMax < (lastIn.pts || 0)) clinch = "e";
        let magic = null;
        if (clinch === "x") magic = 0;                                   // clinched a berth
        else if (!clinch && !notStarted) magic = chaser ? Math.max(0, chaserMax - (s.pts || 0) + 1) : 0;
        s.clinch = clinch;
        s.magic = magic;
    });
    return rows;
}

export function applyStandingsView(rows, period = "combined", rate = "total") {
    if (!Array.isArray(rows)) return rows;
    let out = rows;
    if (period && period !== "combined") {
        const keys = _PERIOD_MAP[period] || [];
        out = out.map((r) => {
            const per = r.per || {};
            const acc = {}; _GOAL_FIELDS.forEach((f) => (acc[f] = 0));
            keys.forEach((k) => { const b = per[k]; if (b) _GOAL_FIELDS.forEach((f) => (acc[f] += (b[f] || 0))); });
            return { ...r, ...acc, gd: acc.gf - acc.ga, _period: period };
        });
    }
    if (rate && rate !== "total") {
        out = out.map((r) => {
            const div = rate === "gp" ? (r.gp || 0) : rate === "p60" ? (r.gp || 0) : rate === "s" ? (r.shots || 0) : 0;
            const rr = { ...r, _rate: rate };
            if (!div) return rr;
            _RATE_FIELDS.forEach((f) => { if (typeof r[f] === "number") rr[f] = Number((r[f] / div).toFixed(2)); });
            rr.gd = Number(((r.gd || 0) / div).toFixed(2));
            return rr;
        });
    }
    return out;
}
